import React, { useState } from 'react';
import API_BASE from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { applyUserLanguagePreference, setShiftsDefaultView } from '../utils/userPreferences';

const ACTORS: { key: string; name: string }[] = [
  { key: 'owner', name: 'Miroslav Šéfl' },
  { key: 'manager', name: 'Petr Řízek' },
  { key: 'emp-bar', name: 'Tomáš Pípa' },
  { key: 'emp-serve', name: 'Klára Podnosová' },
  { key: 'emp-wh', name: 'Jakub Paleta' },
  { key: 'emp-host', name: 'Nikola Úsměvová' },
];

const DemoSessionBanner: React.FC = () => {
  const { user, setUser } = useAuth();
  const { t } = useLanguage();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  if (!user?.isDemo) return null;

  const applyUser = (next: typeof user) => {
    if (!next) return;
    setUser(next);
    localStorage.setItem('user', JSON.stringify(next));
    if (next.shiftsDefaultView === 'list' || next.shiftsDefaultView === 'calendar') {
      setShiftsDefaultView(next.shiftsDefaultView, next.id);
    }
    applyUserLanguagePreference(next);
    window.dispatchEvent(new Event('userUpdated'));
  };

  const switchActor = async (actor: string) => {
    if (actor === user.demoActorKey) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/demo/switch-actor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ actor }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.user) throw new Error(data.error || 'Switch failed');
      applyUser(data.user);
      toast.success(t('demo.session.switched'));
      window.location.assign('/dashboard');
    } catch (err: any) {
      toast.error(err?.message || t('demo.session.switchFailed'));
    } finally {
      setBusy(false);
    }
  };

  const resetDemo = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/demo/reset`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Reset failed');
      toast.success(t('demo.session.resetOk'));
      window.location.reload();
    } catch (err: any) {
      toast.error(err?.message || t('demo.session.resetFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="sticky top-0 z-40 mb-3 rounded-xl border border-[#0065F4]/25 bg-blue-50 px-4 py-2.5 text-sm text-blue-950 flex flex-wrap items-center gap-2"
      role="status"
      data-demo="session-banner"
    >
      <span className="font-semibold">{t('demo.session.banner.title')}</span>
      <span className="text-blue-700/80">·</span>
      <span className="flex-1 min-w-[12rem]">{t('demo.session.banner.body')}</span>
      <label className="flex items-center gap-1.5 text-xs font-medium">
        <span className="text-blue-800/80">{t('demo.session.switch')}</span>
        <select
          disabled={busy}
          value={user.demoActorKey || 'owner'}
          onChange={(e) => switchActor(e.target.value)}
          className="rounded-lg border border-blue-200 bg-white px-2 py-1 text-sm text-gray-800"
        >
          {ACTORS.map((a) => (
            <option key={a.key} value={a.key}>
              {a.name}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        disabled={busy}
        onClick={resetDemo}
        className="rounded-lg border border-blue-200 bg-white px-3 py-1 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-50"
      >
        {t('demo.session.reset')}
      </button>
    </div>
  );
};

export default DemoSessionBanner;
